import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useBar } from "./BarContext";
import { useUserRole } from "./UserRoleContext";
import { get as apiGet } from "../services/api";

const BarOwnerDataContext = createContext();

export const useBarOwnerData = () => {
  const context = useContext(BarOwnerDataContext);
  if (!context) {
    throw new Error(
      "useBarOwnerData must be used within a BarOwnerDataProvider"
    );
  }
  return context;
};

export const BarOwnerDataProvider = ({ children }) => {
  const { selectedBar, userBars } = useBar();
  const { userRole } = useUserRole();

  // Cache states
  // My Artists cache: { [barId]: { artists: [], loading: boolean, error: string } }
  const [myArtistsCache, setMyArtistsCache] = useState({});

  // Find Artists cache: { [cacheKey]: { artists: [], loading: boolean, error: string, hasFetched: boolean } }
  const [findArtistsCache, setFindArtistsCache] = useState({});

  // Management Artists (owner's artists) cache: { artists: [], associations: {}, loading, error, hasFetched }
  const [managementArtistsCache, setManagementArtistsCache] = useState({
    artists: [],
    associations: {},
    loading: false,
    error: null,
    hasFetched: false,
  });

  // Artist members cache for modals: { [artistId]: { members: [], loading, error, hasFetched } }
  const [artistMembersCache, setArtistMembersCache] = useState({});

  // Only trigger owner-artists load once per session until invalidated (for Option 3)
  const ownerArtistsLoadTriggeredRef = useRef(false);

  // Bar admins load on app init (when bars are available)
  const barAdminsLoadTriggeredRef = useRef(false);

  // Bar Admins cache: { adminsByBar: [], loading: boolean, error: string }
  const [barAdminsCache, setBarAdminsCache] = useState({
    adminsByBar: [],
    loading: false,
    error: null,
  });

  // Pending admin invites cache: { invites: [], loading, error, hasFetched }
  const [pendingAdminInvitesCache, setPendingAdminInvitesCache] = useState({
    invites: [],
    loading: false,
    error: null,
    hasFetched: false,
  });

  // Bar Info cache: { bars: [], loading, error, hasFetched }
  const [barsCache, setBarsCache] = useState({
    bars: [],
    loading: false,
    error: null,
    hasFetched: false,
  });

  // Profile cache: { user: {}, loading: boolean, error: string }
  const [profileCache, setProfileCache] = useState({
    user: null,
    loading: false,
    error: null,
  });

  // Track previous bar to detect changes
  const previousBarIdRef = useRef(null);

  // Clear bar-specific caches when bar changes
  useEffect(() => {
    const currentBarId = selectedBar?.bar_id;
    if (
      previousBarIdRef.current !== null &&
      previousBarIdRef.current !== currentBarId
    ) {
      // Bar changed, clear bar-specific caches
      setMyArtistsCache({});
      setFindArtistsCache({});
    }
    previousBarIdRef.current = currentBarId;
  }, [selectedBar?.bar_id]);

  // Fetch My Artists for a bar
  const fetchMyArtists = useCallback(async (barId) => {
    if (!barId) return;

    // Set loading state
    setMyArtistsCache((prev) => ({
      ...prev,
      [barId]: { ...prev[barId], loading: true, error: null },
    }));

    try {
      const data = await apiGet(`/artists/bar/${barId}`);

      if (data.success) {
        setMyArtistsCache((prev) => ({
          ...prev,
          [barId]: {
            artists: data.artists || [],
            loading: false,
            error: null,
            hasFetched: true,
          },
        }));
      } else {
        setMyArtistsCache((prev) => ({
          ...prev,
          [barId]: {
            artists: [],
            loading: false,
            error: data.error || "Failed to fetch artists",
            hasFetched: true,
          },
        }));
      }
    } catch (err) {
      console.error("Error fetching bar artists:", err);
      setMyArtistsCache((prev) => ({
        ...prev,
        [barId]: {
          artists: [],
          loading: false,
          error: err.message,
          hasFetched: true,
        },
      }));
    }
  }, []);

  // Get cached My Artists data
  const getMyArtists = useCallback(
    (barId) => {
      if (!barId)
        return { artists: [], loading: false, error: null, hasFetched: false };
      const cached = myArtistsCache[barId];
      if (!cached) {
        return { artists: [], loading: false, error: null, hasFetched: false };
      }
      return { ...cached, hasFetched: true };
    },
    [myArtistsCache]
  );

  // Fetch Find Artists data
  const fetchFindArtists = useCallback(
    async (barId, searchParams = {}) => {
      if (!barId) return;

      const { search = "", genre = "", radius = "25" } = searchParams;
      const cacheKey = `${barId}_${search}_${genre}_${radius}`;

      // Set loading state
      setFindArtistsCache((prev) => ({
        ...prev,
        [cacheKey]: {
          ...prev[cacheKey],
          loading: true,
          error: null,
          hasFetched: false,
        },
      }));

      if (!selectedBar?.latitude || !selectedBar?.longitude) {
        setFindArtistsCache((prev) => ({
          ...prev,
          [cacheKey]: {
            artists: [],
            loading: false,
            error: "Selected bar does not have location coordinates",
            hasFetched: true,
          },
        }));
        return;
      }

      try {
        const params = new URLSearchParams();
        params.append("barId", barId);
        params.append("radiusMiles", radius);
        if (userRole === "bar_admin") {
          params.append("excludeBarId", String(barId));
        } else {
          params.append("excludeOwnerBars", "1");
        }

        if (genre) {
          params.append("genre", genre);
        }

        if (search.trim()) {
          params.append("search", search.trim());
        }

        const data = await apiGet(`/artists?${params.toString()}`);

        if (data.success) {
          setFindArtistsCache((prev) => ({
            ...prev,
            [cacheKey]: {
              artists: data.artists || [],
              loading: false,
              error: null,
              hasFetched: true,
            },
          }));
        } else {
          setFindArtistsCache((prev) => ({
            ...prev,
            [cacheKey]: {
              artists: [],
              loading: false,
              error: data.error || "Failed to fetch artists",
              hasFetched: true,
            },
          }));
        }
      } catch (err) {
        console.error("Error fetching artists:", err);
        setFindArtistsCache((prev) => ({
          ...prev,
          [cacheKey]: {
            artists: [],
            loading: false,
            error: err.message,
            hasFetched: true,
          },
        }));
      }
    },
    [selectedBar, userRole]
  );

  // Get cached Find Artists data
  const getFindArtists = useCallback(
    (barId, searchParams = {}) => {
      if (!barId)
        return { artists: [], loading: false, error: null, hasFetched: false };
      const { search = "", genre = "", radius = "25" } = searchParams;
      const cacheKey = `${barId}_${search}_${genre}_${radius}`;
      return (
        findArtistsCache[cacheKey] || {
          artists: [],
          loading: false,
          error: null,
          hasFetched: false,
        }
      );
    },
    [findArtistsCache]
  );

  // Fetch Management Artists (all artists across all bars)
  const fetchManagementArtists = useCallback(async () => {
    // Set loading state
    setManagementArtistsCache((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      // Fetch all artists using the same endpoint as FindCovers
      const artistsData = await apiGet("/artists/covers/search?source=my-artists");

      // Fetch bar associations to know which artists belong to which bars
      const associationsData = await apiGet("/bars/artists");

      if (artistsData.success) {
        const artists = artistsData.artists || [];

        // Process associations (include status and invite_id for Manage Artist / pending)
        let associationsMap = {};
        if (associationsData.success && associationsData.artists) {
          associationsData.artists.forEach((barGroup) => {
            barGroup.artists.forEach((artist) => {
              if (!associationsMap[artist.artist_id]) {
                associationsMap[artist.artist_id] = [];
              }
              associationsMap[artist.artist_id].push({
                bar_id: barGroup.bar_id,
                bar_name: barGroup.bar_name,
                status: artist.status || "accepted",
                invite_id: artist.invite_id ?? null,
              });
            });
          });
        }

        setManagementArtistsCache({
          artists,
          associations: associationsMap,
          loading: false,
          error: null,
          hasFetched: true,
        });

        // Prefetch members for these artists (background, capped concurrency)
        const artistIds = artists.map((a) => a.artist_id);
        if (artistIds.length > 0) {
          const CONCURRENCY = 8;
          const fetchOne = async (aid) => {
            try {
              const data = await apiGet(`/artists/${aid}/users`);
              if (data.success && data.users) {
                setArtistMembersCache((prev) => ({
                  ...prev,
                  [aid]: {
                    members: data.users,
                    loading: false,
                    error: null,
                    hasFetched: true,
                  },
                }));
              }
            } catch (e) {
              setArtistMembersCache((prev) => ({
                ...prev,
                [aid]: {
                  members: [],
                  loading: false,
                  error: e.message,
                  hasFetched: true,
                },
              }));
            }
          };
          for (let i = 0; i < artistIds.length; i += CONCURRENCY) {
            const chunk = artistIds.slice(i, i + CONCURRENCY);
            await Promise.all(chunk.map(fetchOne));
          }
        }
      } else {
        setManagementArtistsCache((prev) => ({
          ...prev,
          loading: false,
          error: artistsData.error || "Failed to fetch artists",
          hasFetched: true,
        }));
      }
    } catch (err) {
      console.error("Error fetching management artists:", err);
      setManagementArtistsCache((prev) => ({
        ...prev,
        loading: false,
        error: err.message,
        hasFetched: true,
      }));
    }
  }, []);

  // Get cached Management Artists (owner's artists) data
  const getManagementArtists = useCallback(() => {
    return managementArtistsCache;
  }, [managementArtistsCache]);

  // Trigger load of owner's artists once when a consumer screen (Management or Artist) needs it
  const ensureOwnerArtistsLoaded = useCallback(() => {
    if (
      ownerArtistsLoadTriggeredRef.current ||
      managementArtistsCache.hasFetched ||
      managementArtistsCache.loading
    ) {
      return;
    }
    ownerArtistsLoadTriggeredRef.current = true;
    fetchManagementArtists();
  }, [
    managementArtistsCache.hasFetched,
    managementArtistsCache.loading,
    fetchManagementArtists,
  ]);

  // Get cached artist members (for modal)
  const getArtistMembers = useCallback(
    (artistId) => {
      if (!artistId)
        return { members: [], loading: false, error: null, hasFetched: false };
      const cached = artistMembersCache[artistId];
      if (!cached)
        return { members: [], loading: false, error: null, hasFetched: false };
      return {
        members: cached.members ?? [],
        loading: !!cached.loading,
        error: cached.error ?? null,
        hasFetched: !!cached.hasFetched,
      };
    },
    [artistMembersCache]
  );

  // Fetch artist members (e.g. when opening modal for Find result)
  const fetchArtistMembers = useCallback(
    async (artistId, force = false) => {
      if (!artistId) return null;
      const cached = artistMembersCache[artistId];
      if (!force && cached?.hasFetched) return cached.members ?? null;

      setArtistMembersCache((prev) => ({
        ...prev,
        [artistId]: { ...prev[artistId], loading: true, error: null },
      }));

      try {
        const data = await apiGet(`/artists/${artistId}/users`);
        if (data.success && data.users) {
          setArtistMembersCache((prev) => ({
            ...prev,
            [artistId]: {
              members: data.users,
              loading: false,
              error: null,
              hasFetched: true,
            },
          }));
          return data.users;
        }
        setArtistMembersCache((prev) => ({
          ...prev,
          [artistId]: {
            members: [],
            loading: false,
            error: data.error || "Failed to load members",
            hasFetched: true,
          },
        }));
        return null;
      } catch (err) {
        setArtistMembersCache((prev) => ({
          ...prev,
          [artistId]: {
            members: [],
            loading: false,
            error: err.message,
            hasFetched: true,
          },
        }));
        return null;
      }
    },
    [artistMembersCache]
  );

  // Invalidate artist members cache for one artist (optional, after Manage Artist Save)
  const invalidateArtistMembers = useCallback((artistId) => {
    if (!artistId) return;
    setArtistMembersCache((prev) => {
      const next = { ...prev };
      delete next[artistId];
      return next;
    });
  }, []);

  // Fetch Bar Admins
  const fetchBarAdmins = useCallback(async () => {
    // Set loading state
    setBarAdminsCache((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      const data = await apiGet("/bars/admins");

      if (data.success) {
        setBarAdminsCache({
          adminsByBar: data.admins || [],
          loading: false,
          error: null,
        });
      } else {
        setBarAdminsCache((prev) => ({
          ...prev,
          loading: false,
          error: data.error || "Failed to fetch admins",
        }));
      }
    } catch (err) {
      console.error("Error fetching admins:", err);
      setBarAdminsCache((prev) => ({
        ...prev,
        loading: false,
        error: err.message,
      }));
    }
  }, []);

  // Get cached Bar Admins data
  const getBarAdmins = useCallback(() => {
    return barAdminsCache;
  }, [barAdminsCache]);

  // Load bar admins on app init when bar owner has bars (owner-only API)
  useEffect(() => {
    if (userRole !== "bar_owner") return;
    if (
      userBars?.length > 0 &&
      !barAdminsLoadTriggeredRef.current &&
      !barAdminsCache.loading
    ) {
      barAdminsLoadTriggeredRef.current = true;
      fetchBarAdmins();
    }
  }, [
    userRole,
    userBars?.length,
    barAdminsCache.loading,
    fetchBarAdmins,
  ]);

  // Fetch pending admin invites
  const fetchPendingAdminInvites = useCallback(async () => {
    setPendingAdminInvitesCache((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      const data = await apiGet("/invites/sent/admin");

      if (data.success) {
        setPendingAdminInvitesCache({
          invites: data.invites || [],
          loading: false,
          error: null,
          hasFetched: true,
        });
      } else {
        setPendingAdminInvitesCache((prev) => ({
          ...prev,
          loading: false,
          error: data.error || "Failed to load invites",
          hasFetched: true,
        }));
      }
    } catch (err) {
      console.error("Error fetching pending admin invites:", err);
      setPendingAdminInvitesCache((prev) => ({
        ...prev,
        loading: false,
        error: err.message,
        hasFetched: true,
      }));
    }
  }, []);

  const getPendingAdminInvites = useCallback(() => {
    return pendingAdminInvitesCache;
  }, [pendingAdminInvitesCache]);

  // Invalidate and refetch pending invites (e.g. on adminInvitesUpdated)
  const invalidatePendingAdminInvites = useCallback(() => {
    setPendingAdminInvitesCache((prev) => ({
      ...prev,
      hasFetched: false,
    }));
    fetchPendingAdminInvites();
  }, [fetchPendingAdminInvites]);

  // Listen for adminInvitesUpdated to invalidate pending invites cache
  useEffect(() => {
    const handleAdminInvitesUpdated = () => {
      invalidatePendingAdminInvites();
    };
    window.addEventListener("adminInvitesUpdated", handleAdminInvitesUpdated);
    return () =>
      window.removeEventListener("adminInvitesUpdated", handleAdminInvitesUpdated);
  }, [invalidatePendingAdminInvites]);

  // Fetch Bars list
  const fetchBars = useCallback(async () => {
    // Set loading state
    setBarsCache((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      const data = await apiGet("/bars");

      if (data.success) {
        setBarsCache({
          bars: data.bars || [],
          loading: false,
          error: null,
          hasFetched: true,
        });
      } else {
        setBarsCache((prev) => ({
          ...prev,
          loading: false,
          error: data.error || "Failed to fetch bars",
          hasFetched: true,
        }));
      }
    } catch (err) {
      console.error("Error fetching bars:", err);
      setBarsCache((prev) => ({
        ...prev,
        loading: false,
        error: err.message,
        hasFetched: true,
      }));
    }
  }, []);

  // Get cached Bars data
  const getBars = useCallback(() => {
    return barsCache;
  }, [barsCache]);

  // Fetch Profile data
  const fetchProfile = useCallback(async () => {
    // Set loading state
    setProfileCache((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      const data = await apiGet("/users/profile");

      if (data.success) {
        setProfileCache({
          user: data.user,
          loading: false,
          error: null,
        });
      } else {
        setProfileCache((prev) => ({
          ...prev,
          loading: false,
          error: data.error || "Failed to fetch profile",
        }));
      }
    } catch (err) {
      console.error("Error fetching profile:", err);
      setProfileCache((prev) => ({
        ...prev,
        loading: false,
        error: err.message,
      }));
    }
  }, []);

  // Get cached Profile data
  const getProfile = useCallback(() => {
    return profileCache;
  }, [profileCache]);

  // Invalidate specific cache
  const invalidateCache = useCallback((cacheType, barId = null) => {
    switch (cacheType) {
      case "myArtists":
        if (barId) {
          setMyArtistsCache((prev) => {
            const newCache = { ...prev };
            delete newCache[barId];
            return newCache;
          });
        } else {
          setMyArtistsCache({});
        }
        break;
      case "findArtists":
        if (barId) {
          // Remove all cache keys for this bar
          setFindArtistsCache((prev) => {
            const newCache = {};
            Object.keys(prev).forEach((key) => {
              if (!key.startsWith(`${barId}_`)) {
                newCache[key] = prev[key];
              }
            });
            return newCache;
          });
        } else {
          setFindArtistsCache({});
        }
        break;
      case "managementArtists":
        ownerArtistsLoadTriggeredRef.current = false;
        setManagementArtistsCache({
          artists: [],
          associations: {},
          loading: false,
          error: null,
          hasFetched: false,
        });
        break;
      case "artistMembers":
        if (barId) {
          setArtistMembersCache((prev) => {
            const next = { ...prev };
            delete next[barId];
            return next;
          });
        } else {
          setArtistMembersCache({});
        }
        break;
      case "barAdmins":
        barAdminsLoadTriggeredRef.current = false;
        setBarAdminsCache({
          adminsByBar: [],
          loading: false,
          error: null,
        });
        break;
      case "bars":
        setBarsCache({
          bars: [],
          loading: false,
          error: null,
          hasFetched: false,
        });
        break;
      case "profile":
        setProfileCache({
          user: null,
          loading: false,
          error: null,
        });
        break;
      case "pendingAdminInvites":
        setPendingAdminInvitesCache({
          invites: [],
          loading: false,
          error: null,
          hasFetched: false,
        });
        break;
      default:
        console.warn(`Unknown cache type: ${cacheType}`);
    }
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const barId = e.detail?.barId;
      if (!barId) return;
      invalidateCache("myArtists", barId);
      invalidateCache("findArtists", barId);
      invalidateCache("managementArtists");
      fetchManagementArtists();
      if (selectedBar?.bar_id == barId) {
        fetchMyArtists(barId);
      }
    };
    window.addEventListener("artistRelationshipsUpdated", handler);
    return () =>
      window.removeEventListener("artistRelationshipsUpdated", handler);
  }, [
    invalidateCache,
    fetchManagementArtists,
    fetchMyArtists,
    selectedBar?.bar_id,
  ]);

  // Clear all caches
  const clearCache = useCallback(() => {
    ownerArtistsLoadTriggeredRef.current = false;
    barAdminsLoadTriggeredRef.current = false;
    setMyArtistsCache({});
    setFindArtistsCache({});
    setManagementArtistsCache({
      artists: [],
      associations: {},
      loading: false,
      error: null,
      hasFetched: false,
    });
    setArtistMembersCache({});
    setBarAdminsCache({
      adminsByBar: [],
      loading: false,
      error: null,
    });
    setBarsCache({
      bars: [],
      loading: false,
      error: null,
      hasFetched: false,
    });
    setProfileCache({
      user: null,
      loading: false,
      error: null,
    });
    setPendingAdminInvitesCache({
      invites: [],
      loading: false,
      error: null,
      hasFetched: false,
    });
  }, []);

  const value = {
    // My Artists (legacy per-bar fetch; My Artists tab will use owner artists filtered by bar)
    fetchMyArtists,
    getMyArtists,
    // Find Artists
    fetchFindArtists,
    getFindArtists,
    // Management / Owner Artists (single source for Management tab and My Artists tab)
    fetchManagementArtists,
    getManagementArtists,
    ensureOwnerArtistsLoaded,
    // Artist members (for modal)
    getArtistMembers,
    fetchArtistMembers,
    invalidateArtistMembers,
    // Bar Admins
    fetchBarAdmins,
    getBarAdmins,
    // Pending Admin Invites
    fetchPendingAdminInvites,
    getPendingAdminInvites,
    // Bars
    fetchBars,
    getBars,
    // Profile
    fetchProfile,
    getProfile,
    // Cache management
    invalidateCache,
    clearCache,
  };

  return (
    <BarOwnerDataContext.Provider value={value}>
      {children}
    </BarOwnerDataContext.Provider>
  );
};
